import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { ApiEndpoints, PageNames, Routes } from '../../../../utils/app-constants';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageBody } from '../../../../components/page-components/PageBody';
import { navigateToPage } from '../../../../utils/history';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { PhysicalVerticalEntry, PhysicalVerticalEntryPart, VerticalInventoryQuantityRow } from '../../../../modules/inventory/models/physical-control-group-entry';
import { StatusCode } from '../../../../utils/api';
import { InventoryEntryUpdate } from '../../../../modules/inventory/models/physical-inventory-entry-update';
import { AutoComplete } from '../../../../containers/autocomplete/AutoComplete';
import { useDispatch } from 'react-redux';
import { InventoryPartAutocompleteItem } from '../../../../modules/inventory/models/inventory-part-autocomplete-item';
import { AutoCompleteItem } from '../../../../models/utilities/autocomplete-item';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { PhysicalControlGroupStatus } from '../../../../modules/inventory/models/physical-inventory-details';
import { PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';
import { getQuantity } from '../../../../modules/inventory/models/utils';

interface IProps {
    location: any;
}

export const InventoryPhysicalVerticalEntryEditScene = React.forwardRef((props: IProps, ref) => {

    const dispatch = useDispatch();
    const [service] = React.useState(new InventorySvc());
    const [entry, setEntry] = React.useState(new PhysicalVerticalEntry());

    const [newPart, setNewPart] = React.useState<InventoryPartAutocompleteItem | null>(null);

    function goBack() {
        var reportId = getReportId();
        navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
    }

    async function getEntry(reportId: string, controlNumber: string, entryNumber: number) {

        if (controlNumber) {
            const result = await service.getControlGroupEntry(reportId, controlNumber, entryNumber);
            if (result.status === StatusCode.Success) {
                var entry = new PhysicalVerticalEntry(result.data);
                setEntry(entry);
                return;
            }
        }
        else {
            goBack();
        }
    }

    function save(complete: boolean) {

        var reportId = getReportId();

        var payload = {
            reportId: reportId,
            controlNumber: entry.controlNumber,
            entryNumber: entry.entry,
            name: entry.name,
            serviceCenter: entry.serviceCenter,
            complete: complete,
            parts: entry.parts.map(p => {
                return {
                    partNumber: p.partNumber,
                    quantities: p.getQuantities()
                };
            })
        } as InventoryEntryUpdate;

        service.updateControlGroupEntry(reportId, payload)
            .then((result) => {
                if (result.status === StatusCode.Success) {
                    getEntry(getReportId(), getControlNumber(), Number.parseInt(getEntryNumber(), 10));
                }
            });
    }

    function disableEdit(): boolean {
        return entry.status == PhysicalControlGroupStatus.complete || entry.reportStatus == PhysicalInventoryStatus.complete;
    }

    const getTopButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalControlGroupsEdit
        ]}>
            <button className="btn attention" onClick={() => save(false)} disabled={disableEdit()}>
                SAVE
            </button>
        </HasPermission>;
    }

    const getBottomButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalControlGroupsEdit
        ]}>
            <button className="btn complete" onClick={() => save(true)} disabled={disableEdit()}>
                COMPLETE
            </button>
        </HasPermission>;
    }

    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    function getControlNumber() {
        const opts = props.location.pathname.split('/');
        return opts.length > 8 ? opts[8] : null;
    }

    function getEntryNumber() {
        const opts = props.location.pathname.split('/');
        return opts.length > 9 ? opts[9] : null;
    }

    function addQuantityRow(partIdx: number)
    {
        if (entry.parts.length > partIdx) {
            var tmpEntry = { ...entry };
            var part = tmpEntry.parts[partIdx];
            part.quantities.push(new VerticalInventoryQuantityRow());
            setEntry(tmpEntry);
        }
    }

    function addItem() {
        if (newPart) {
            var tmpEntry = { ...entry };

            var part = new PhysicalVerticalEntryPart();
            part.partNumber = newPart.partNumber;
            part.type = newPart.type;
            part.subtype = newPart.subtype;
            part.unitOfMeasure = newPart.unitOfMeasure;
            part.description = newPart.description;
            part.isNew = true;

            // finish out the rows
            for (let i=0; i < 6; i++) {
                part.quantities.push(new VerticalInventoryQuantityRow());
            }

            tmpEntry.parts.push(part);
            setEntry(tmpEntry);
            setNewPart(null);
        }
    }

    function removeItem(idx: number) {
        var tmpEntry = { ...entry };
        tmpEntry.parts.splice(idx, 1);
        setEntry(tmpEntry);
    }

    function onPartSelected(e: AutoCompleteItem) {
        if (e?.extraData?.id) {
            setNewPart(e.extraData as InventoryPartAutocompleteItem)
        }
    }

    function handleBlur(e: React.ChangeEvent<HTMLInputElement>, partIdx: number, qtyIdx: number) {
        if (entry.parts.length > partIdx) {
            var tmpEntry = { ...entry };
            var part = tmpEntry.parts[partIdx];
            if (e?.target && part.quantities.length > qtyIdx) {
                if (e.target.name == "quantity1") {
                    part.quantities[qtyIdx].quantity1 = getQuantity(part.quantities[qtyIdx].quantity1);
                }
                if (e.target.name == "quantity2") {
                    part.quantities[qtyIdx].quantity2 = getQuantity(part.quantities[qtyIdx].quantity2);
                }

                setEntry(tmpEntry);
            }
        }
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>, partIdx: number, qtyIdx: number) {
        if (entry.parts.length > partIdx) {
            var tmpEntry = { ...entry };
            var part = tmpEntry.parts[partIdx];
            if (e?.target && part.quantities.length > qtyIdx ) {
                if (e.target.name == "quantity1") {
                    part.quantities[qtyIdx].quantity1 = e.target.value;
                }
                if (e.target.name == "quantity2") {
                    part.quantities[qtyIdx].quantity2 = e.target.value;
                }

                setEntry(tmpEntry);
            }
        }
    }

    React.useEffect(() => {
        getEntry(getReportId(), getControlNumber(), Number.parseInt(getEntryNumber(), 10));
    }, [props.location]);

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_CONTROL_GROUP_ENTRY} classes='inventory-physical'>
        <PageBody>
            <div className="row">
                <div className="col">
                    {getTopButtons()}
                </div>
            </div>
            <div className="row">
                <div className="col-2">
                    <span className="invnetory-label">Control ID</span>
                    <span className="invnetory-value">{entry.controlNumber}</span>
                </div>
                <div className="col-2">
                    <span className="invnetory-label">Entry</span>
                    <span className="invnetory-value">{entry.entry}</span>
                </div>
                <div className="col-5">
                    <span className="invnetory-label">Control Name</span>
                    <span className="invnetory-value"> {entry.name} </span>
                </div>
                <div className="col-3">
                    <span className="invnetory-label">Service Center</span>
                    <span className="invnetory-value"> {entry.serviceCenter} </span>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    {entry.parts.map((part, partIdx) => (
                        <div className="vertical-part-edit" key={partIdx}>

                            <div className="row">
                                <div className="col">
                                    <span className="part-number-header">PART# {part.partNumber}</span>
                                    <span className="part-description-header">{part.description}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>COLUMN 1</th>
                                                <th>COLUMN 2</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                part.quantities.map((qty, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        <td>{rowIdx+1}</td>
                                                        <td>
                                                            <input
                                                                tabIndex={partIdx * 100 + 1 + rowIdx}
                                                                id={"quantity1"}
                                                                className="form-control"
                                                                name={"quantity1"}
                                                                value={qty.quantity1}
                                                                min="0"
                                                                disabled={disableEdit()}
                                                                onChange={(e) => handleChange(e, partIdx, rowIdx)}
                                                                onBlur={(e) => handleBlur(e, partIdx, rowIdx)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                tabIndex={partIdx * 100 + 50 + rowIdx}
                                                                id={"quantity2"}
                                                                className="form-control"
                                                                name={"quantity2"}
                                                                value={qty.quantity2}
                                                                min="0"
                                                                disabled={disableEdit()}
                                                                onChange={(e) => handleChange(e, partIdx, rowIdx)}
                                                                onBlur={(e) => handleBlur(e, partIdx, rowIdx)}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            <tr>
                                                <td colSpan={3}>
                                                    <button
                                                        id="add-qty"
                                                        onClick={(e) => addQuantityRow(partIdx)}
                                                        type="button"
                                                        disabled={disableEdit()}
                                                        className="btn-icon add-item-btn" >
                                                        <i className="la la-plus-circle" />
                                                        Add Row
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>                        
                    ))}
                    <div className="vertical-part-edit">
                        <div className="row">
                            <div className="col">
                                <AutoComplete
                                    id="part-autocomplete"
                                    dispatch={dispatch}
                                    onSelectedCallback={onPartSelected}
                                    placeholder="Part #"
                                    currentValue={newPart ? newPart.partNumber : ''}
                                    suggestionRender={'partNumber'}
                                    disabled={disableEdit()}
                                    endpoint={ApiEndpoints.INVENTORY_PARTS_AUTOCOMPLETE.formattify(getReportId())}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col">
                                <button
                                    id="add-item"
                                    onClick={addItem}
                                    type="button"
                                    disabled={disableEdit()}
                                    className="btn-icon add-item-btn" >
                                    <i className="la la-plus-circle" />
                                    Add Item
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    {getBottomButtons()}
                </div>
            </div>
        </PageBody>
    </PageContent>
});